import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLang } from '@/i18n/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Stethoscope, Award, CreditCard, Wallet, ShieldCheck, ChevronRight, X, Banknote, Smartphone, DollarSign } from 'lucide-react';

const programDetails = {
  en: {
    'Dental Assistant': {
      title: 'Dental Assistant I Program',
      desc: [
        "Dental assistants play a crucial role in ensuring dental offices operate efficiently while enhancing patient comfort and care. Imagine the fulfillment of helping individuals achieve healthy, confident smiles.",
        "If you're passionate about dentistry and seeking a fulfilling career without the long commitment to schooling, Royal Dental Assistant Academy's program could be an ideal choice for you.",
        "Cost: $3,650"
      ],
      img: '/images/gallery/Captura de pantalla 2026-07-06 a la(s) 8.10.06 a. m..png',
      link: 'https://form.jotform.com/232094520728051'
    },
    'Orthodontic Assistant': {
      title: 'Orthodontic Assistant Program',
      desc: [
        "Specializing as an Orthodontic Assistant allows you to work closely with orthodontists in helping patients achieve perfectly aligned smiles.",
        "Our specialty program covers everything from taking precise impressions to assisting with braces and clear aligners.",
        "Cost: $4,550"
      ],
      img: '/images/gallery/IMG_4100.jpg',
      link: 'https://form.jotform.com/232094520728051'
    },
    'NC Radiology': {
      title: 'NC Radiology',
      desc: [
        "Our Teeth Radiology Program is designed to equip students with the essential skills and knowledge to safely and effectively perform dental radiographic procedures.",
        "By the end of the program, students will be prepared to assist dental professionals in capturing accurate diagnostic images and ensuring a safe environment for both patients and staff. This program is perfect for individuals looking to enhance their dental assisting credentials or start a specialized career in dental radiography.",
        "Cost: $650"
      ],
      img: '/images/gallery/radiology_program.png',
      link: 'https://form.jotform.com/242294621286055'
    },
    'Coronal Polishing': {
      title: 'Coronal Polishing',
      desc: [
        "Coronal polishing technicians play an essential role in maintaining oral hygiene by polishing teeth surfaces and enhancing the look and feel of a patient's smile.",
        "If you're interested in the dental field and want to start a hands-on career quickly, becoming a certified coronal polishing technician could be the ideal path for you.",
        "Cost: $375"
      ],
      img: '/images/gallery/student_practice_new.png',
      link: 'https://form.jotform.com/242294621286055'
    },
    'CPR': {
      title: 'CPR Certification',
      desc: [
        "Take advantage of our continued education to further your skills and career.",
        "Our CPR course is tailored for dental professionals to handle emergencies effectively in a clinical setting.",
        "Cost: $50"
      ],
      img: '/images/gallery/IMG_6482.jpg',
      link: 'https://form.jotform.com/242294621286055'
    },
    'Radiology Challenge Exam': {
      title: 'Radiology Challenge Exam',
      desc: [
        "Take advantage of our continued education to further your skills and career.",
        "Get prepared and certified with our specialized Radiology Challenge Exam training.",
        "Cost: $350"
      ],
      img: '/images/gallery/IMG_6484.jpg',
      link: 'https://form.jotform.com/242294621286055'
    },
    'SPICE': {
      title: 'SPICE Certification',
      desc: [
        "Take advantage of our continued education to further your skills and career.",
        "SPICE (Statewide Program for Infection Control and Epidemiology) certification is crucial for maintaining the highest standards of safety in the dental office.",
        "Cost: $65"
      ],
      img: '/graduates/grad-1.png',
      link: 'https://form.jotform.com/242294621286055'
    },
    'Continued Education': {
      title: 'Continued Education',
      desc: [
        "Take advantage of our continued education to further your skills and career.",
        "• CPR Certification",
        "• SPICE",
        "• Radiology Challenge Exam",
        "• Coronal Polishing"
      ],
      img: '/images/gallery/IMG_7880.jpg',
      link: 'https://form.jotform.com/242294621286055'
    }
  },
  es: {
    'Dental Assistant': {
      title: 'Programa de Asistente Dental I',
      desc: [
        "Las asistentes dentales juegan un papel crucial en garantizar que los consultorios dentales funcionen eficientemente, mejorando el confort y la atención al paciente.",
        "Si tienes pasión por la odontología y buscas una carrera gratificante sin años de estudio, el programa de Royal Dental Assistant Academy podría ser la opción ideal para ti.",
        "Costo: $3,650"
      ],
      img: '/images/gallery/IMG_4099.jpg',
      link: 'https://form.jotform.com/232094520728051'
    },
    'Orthodontic Assistant': {
      title: 'Programa de Asistente de Ortodoncia',
      desc: [
        "Especializarte como Asistente de Ortodoncia te permite trabajar de cerca con ortodoncistas ayudando a los pacientes a lograr sonrisas perfectamente alineadas.",
        "Nuestro programa de especialidad cubre todo, desde tomar impresiones precisas hasta asistir con brackets y alineadores transparentes.",
        "Costo: $4,550"
      ],
      img: '/images/gallery/IMG_4100.jpg',
      link: 'https://form.jotform.com/232094520728051'
    },
    'NC Radiology': {
      title: 'Radiología de NC',
      desc: [
        "Nuestro Programa de Radiología Dental está diseñado para equipar a los estudiantes con las habilidades y conocimientos esenciales para realizar procedimientos radiográficos dentales de forma segura y efectiva.",
        "Al finalizar el programa, los estudiantes estarán preparados para asistir a los profesionales dentales en la captura de imágenes diagnósticas precisas. Perfecto para quienes desean mejorar sus credenciales.",
        "Costo: $650"
      ],
      img: '/images/gallery/radiology_program.png',
      link: 'https://form.jotform.com/242294621286055'
    },
    'Coronal Polishing': {
      title: 'Pulido Coronal',
      desc: [
        "Los técnicos en pulido coronal juegan un papel esencial en el mantenimiento de la higiene bucal al pulir las superficies dentales y mejorar la apariencia de la sonrisa del paciente.",
        "Si te interesa el campo dental y quieres iniciar una carrera práctica rápidamente, convertirte en técnico certificado de pulido coronal puede ser el camino ideal para ti.",
        "Costo: $375"
      ],
      img: '/images/gallery/student_practice_new.png',
      link: 'https://form.jotform.com/242294621286055'
    },
    'CPR': {
      title: 'Certificación de RCP',
      desc: [
        "Aprovecha nuestra educación continua para mejorar tus habilidades y carrera.",
        "Nuestro curso de RCP está diseñado para profesionales dentales para manejar emergencias de forma efectiva en un entorno clínico.",
        "Costo: $50"
      ],
      img: '/images/gallery/IMG_6482.jpg',
      link: 'https://form.jotform.com/242294621286055'
    },
    'Radiology Challenge Exam': {
      title: 'Examen de Desafío de Radiología',
      desc: [
        "Aprovecha nuestra educación continua para mejorar tus habilidades y carrera.",
        "Prepárate y certifícate con nuestro entrenamiento especializado para el Examen de Desafío de Radiología.",
        "Costo: $350"
      ],
      img: '/images/gallery/IMG_6484.jpg',
      link: 'https://form.jotform.com/242294621286055'
    },
    'SPICE': {
      title: 'Certificación SPICE',
      desc: [
        "Aprovecha nuestra educación continua para mejorar tus habilidades y carrera.",
        "La certificación SPICE (Programa Estatal de Control de Infecciones y Epidemiología) es crucial para mantener los más altos estándares de seguridad en el consultorio dental.",
        "Costo: $65"
      ],
      img: '/graduates/grad-1.png',
      link: 'https://form.jotform.com/242294621286055'
    },
    'Continued Education': {
      title: 'Educación Continua',
      desc: [
        "Aprovecha nuestra educación continua para mejorar tus habilidades y carrera.",
        "• Certificación de RCP",
        "• SPICE",
        "• Examen de Desafío de Radiología",
        "• Pulido Coronal"
      ],
      img: '/images/gallery/IMG_7880.jpg',
      link: 'https://form.jotform.com/242294621286055'
    }
  }
};

const ProgramsAndTuitionTabs = ({ defaultTab = 'programs' }) => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [selectedProgram, setSelectedProgram] = useState(null);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const details = programDetails[lang] || programDetails.en;

  const getProviderLogo = (provider) => {
    const p = provider.toLowerCase();
    if (p.includes('affirm')) return <span className="font-extrabold text-[#000000] text-2xl tracking-tighter lowercase">affirm</span>;
    if (p.includes('klarna')) return <span className="font-bold text-black text-2xl tracking-tight">Klarna<span className="text-[#FFB3C7]">.</span></span>;
    if (p.includes('paypal')) return <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" className="h-6 object-contain" />;
    if (p.includes('afterpay')) return <span className="font-extrabold text-[#B2FCE4] bg-black px-2 py-0.5 rounded-md text-xl tracking-tight">afterpay<span className="text-white">♡</span></span>;
    return <span>{provider}</span>;
  };


  return (
    <section id="programs" className="py-16 md:py-24 bg-slate-50 border-t border-slate-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-secondary/50 border border-brand-primary text-xs font-semibold text-brand-primary uppercase tracking-wider mb-5">
            {activeTab === 'programs' 
              ? (lang === 'en' ? 'Our Programs' : 'Nuestros Programas')
              : (lang === 'en' ? 'Tuition & Financing' : 'Colegiatura y Financiamiento')}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mb-5">
            {activeTab === 'programs'
              ? t.programsTabs.title
              : (lang === 'en' ? 'Invest In Your Future' : 'Invierte en tu Futuro')}
          </h2>
        </div>

        {/* Tab Content */}
        <div className="relative min-h-[400px]">
          <AnimatePresence mode="wait">
            
            {/* PROGRAMS TAB */}
            {activeTab === 'programs' && (
              <motion.div
                key="programs"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="max-w-3xl mx-auto"
              >

                {/* Continuing Education */}
                <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200 h-full">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 rounded-2xl bg-brand-secondary flex items-center justify-center shrink-0">
                      <Award className="w-6 h-6 text-brand-primary" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">{t.programsTabs.contEdLabel}</h3>
                  </div>
                  <ul className="space-y-3">
                    {t.programsTabs.contEdPrograms.map((course, idx) => {
                      const programIds = ['Dental Assistant', 'NC Radiology', 'Coronal Polishing', 'Continued Education'];
                      return (
                        <li 
                          key={idx} 
                          onClick={() => setSelectedProgram(details[programIds[idx]] || details['Dental Assistant'])}
                          className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-transparent hover:border-brand-primary hover:shadow-md transition-all cursor-pointer group"
                        >
                          <ChevronRight className="w-5 h-5 text-brand-primary shrink-0 group-hover:translate-x-1 transition-transform" />
                          <span className="font-semibold text-slate-700 group-hover:text-brand-primary transition-colors">{course}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </motion.div>
            )}

            {/* TUITION TAB */}
            {activeTab === 'tuition' && (
              <motion.div
                key="tuition"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                  {/* 1. In House */}
                  <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200 flex flex-col h-full">
                    <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                      <Wallet className="w-6 h-6 text-brand-primary" />
                      {t.programsTabs.inHouseLabel}
                    </h3>
                    <div className="space-y-4 flex-grow">
                      {t.programsTabs.inHouseOptions.map((opt, idx) => (
                        <div key={idx} className="p-4 rounded-2xl bg-slate-50 border border-slate-100 relative overflow-hidden group hover:border-brand-primary transition-colors">
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-bold text-slate-800">{opt.title}</span>
                            {opt.amount && <span className="font-extrabold text-brand-primary">{opt.amount}</span>}
                          </div>
                          <p className="text-sm text-brand-gray">{opt.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 2. Third Party */}
                  <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200 flex flex-col h-full">
                    <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                      <CreditCard className="w-6 h-6 text-purple-500" />
                      {t.programsTabs.thirdPartyLabel}
                    </h3>
                    <div className="flex flex-wrap gap-4 flex-grow content-start">
                      {t.programsTabs.thirdPartyOptions.map((provider, idx) => (
                        <div key={idx} className="h-14 px-6 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-center shadow-sm hover:border-brand-primary transition-colors cursor-default">
                          {getProviderLogo(provider)}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 3. Financial Assistance */}
                  <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200 flex flex-col h-full">
                    <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                      <ShieldCheck className="w-6 h-6 text-emerald-500" />
                      {t.programsTabs.financialAssistanceLabel}
                    </h3>
                    <div className="space-y-3 flex-grow">
                      {t.programsTabs.financialAssistanceOptions.map((assist, idx) => (
                        <div key={idx} className="flex flex-col gap-2 p-4 rounded-xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <span className="font-bold text-slate-800 shrink-0">{assist.title}</span>
                            <span className="hidden sm:inline text-slate-300">•</span>
                            <span className="text-sm text-brand-gray">{assist.desc}</span>
                          </div>
                          {assist.extra && (
                            <div className="mt-2 text-sm text-slate-600 border-l-2 border-brand-primary/20 pl-3">
                              <p className="mb-3">{assist.extra}</p>
                              {assist.btnText && assist.btnLink && (
                                <a 
                                  href={assist.btnLink} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-block bg-brand-primary/10 hover:bg-brand-primary text-brand-primary hover:text-white px-4 py-2 rounded-lg font-semibold transition-colors text-xs"
                                >
                                  {assist.btnText}
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 4. Payment Methods */}
                  <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-200 flex flex-col h-full">
                    <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                      <Banknote className="w-6 h-6 text-amber-500" />
                      {t.programsTabs.paymentMethodsLabel}
                    </h3>
                    <div className="space-y-2 flex-grow">
                      {t.programsTabs.paymentMethodsOptions.map((method, idx) => {
                        const Icon = method.icon === 'cash' ? Banknote : method.icon === 'zelle' ? Smartphone : method.icon === 'cashapp' ? DollarSign : CreditCard;
                        return (
                          <div key={idx} className="flex items-center gap-4 py-3 border-b border-slate-100 last:border-0">
                            <div className="text-amber-500">
                              <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex flex-col">
                              <span className="font-semibold text-slate-800">{method.title}</span>
                              {method.desc && <span className="text-xs text-slate-400">{method.desc}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Footer Banner & Apply Now Button */}
                <div className="mt-16 flex flex-col items-center text-center">
                  <div className="inline-flex items-center justify-center flex-wrap gap-2 mb-8 text-slate-600 bg-slate-50 px-6 py-3 rounded-full border border-slate-100">
                    <ShieldCheck className="w-5 h-5 text-brand-primary" />
                    <span>
                      {lang === 'en' ? 'Our team is here to help you find the best option for your needs.' : 'Nuestro equipo está aquí para ayudarte a encontrar la mejor opción.'}
                    </span>
                    <a onClick={(e) => { e.preventDefault(); navigate('/'); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="text-brand-primary font-bold hover:underline cursor-pointer">
                      {lang === 'en' ? 'Contact us' : 'Contáctanos'}
                    </a>
                    <span>
                      {lang === 'en' ? 'with any questions!' : 'con cualquier duda!'}
                    </span>
                  </div>
                  <button 
                    onClick={() => navigate('/application')}
                    className="w-full sm:w-auto bg-brand-primary hover:opacity-90 text-white px-12 py-4 rounded-full font-bold text-lg transition-all shadow-lg shadow-brand-primary/20 hover:scale-105"
                  >
                    {lang === 'en' ? 'Apply Now' : 'Aplica Ahora'}
                  </button>
                </div>

              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>

      {/* Program Details Modal */}
      <AnimatePresence>
        {selectedProgram && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProgram(null)}
              className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100]"
            />
            <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 md:p-8 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl flex flex-col md:flex-row pointer-events-auto relative"
              >
              {/* Close Button */}
              <button 
                onClick={() => setSelectedProgram(null)}
                className="absolute top-4 right-4 p-2 bg-white/50 backdrop-blur-md rounded-full text-slate-600 hover:text-slate-900 hover:bg-white transition-colors z-10"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Image Section */}
              <div className="w-full md:w-1/2 h-64 md:h-auto shrink-0 relative bg-brand-secondary">
                <img 
                  src={selectedProgram.img} 
                  alt={selectedProgram.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content Section */}
              <div className="p-8 md:p-12 flex-1 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-secondary text-xs font-bold text-brand-primary uppercase tracking-wider mb-4 w-max">
                  {activeTab === 'programs' ? (lang === 'es' ? 'Info del Programa' : 'Program Info') : (lang === 'es' ? 'Detalles' : 'Details')}
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                  {selectedProgram.title}
                </h2>
                
                <div className="space-y-4 text-slate-600 mb-8 leading-relaxed">
                  {selectedProgram.desc.map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>

                <div className="mt-auto">
                  <button 
                    onClick={() => {
                      if (selectedProgram.link) {
                        window.open(selectedProgram.link, '_blank');
                      } else {
                        setSelectedProgram(null);
                        const el = document.getElementById('apply');
                        if(el) el.scrollIntoView({behavior: 'smooth'});
                      }
                    }}
                    className="w-full sm:w-auto bg-brand-primary hover:opacity-80 text-white px-8 py-4 rounded-xl font-bold transition-all shadow-lg shadow-teal-900/20"
                  >
                    {t.hero.ctaPrimary}
                  </button>
                </div>
              </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

    </section>
  );
};

export default ProgramsAndTuitionTabs;
